import React from "react";
import { getSdk } from "../../services/sdkService";
import { Loading } from "../../services/visualLibraryService";
import { HeadingStyled, ContainerStyled } from "../../styles/shared-styles";
import { FullCatalogPageStyled, BlockStyled, GridStyled, ImageStyled, SecondaryMenuStyled } from "./styled/Styled";
import { goToDetailPage } from "../../utils/goToDetailPage";
import { i18n, directions } from "../../services/i18nService";
import { withAppState, getAppState } from "../../state";
import { withSubtheme } from "../../styles/themes/withSubtheme";
import { getGlobalButtonFocusPath, getGlobalButtonInfo } from "../../errors/globalButtons";
import { getInternetConnectivityModalConfig } from "../../errors/utils";
import { config } from "../../config/config";
import { resolution } from "../../config/ui/resolutionConfig";
import { trackPage, trackEvent, getFullCatalogEventTrackingData } from "../../services/analyticsService";
import { getPath } from "../../utils/getPath";
import * as Sentry from "@sentry/browser";

const defaultFocusPath = GridStyled.defaultFocusPath;
const LAST_ADDED = "lastAdded";
const PAGE = "fullCatalog";
let sdk = null;

/**
 * Gets default genres/categories to be displayed in the genre list. These menu items will be common
 * for every full catalog page for the time being, except for the movies' full catalog, which will
 * show these ones apart from another bunch of its own.
 */
function getDefaultMenuItems() {
  const items = config.get().defaultGenresMenuItems;
  return items.map(item => ({ ...item, tagTitle: i18n.get(`pages.fullCatalog.${item.name}`) }));
}

/**
 * Handles the error received either from the movies genres retrieval or from the catalogue items
 * retrieval by section name.
 * @param {Error} error - Error thrown.
 */
function handleError(error) {
  const {
    stateActions: { showGlobalModal, hideGlobalModal },
    history,
    setFocus
  } = this.props;
  const retry = () => {
    hideGlobalModal();
    this.fetchData();
  };
  const back = () => {
    hideGlobalModal();
    history.goBack();
  };
  const networkErrorButtonsCallbacks = {
    retryImplementation: retry,
    backImplementation: back
  };
  if (getAppState().isOnline && error.message.indexOf("timeout") > -1) {
    showGlobalModal(
      getInternetConnectivityModalConfig.call(this, {
        ...networkErrorButtonsCallbacks,
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else if (!getAppState().isOnline && !error.status && !error.response) {
    showGlobalModal(getInternetConnectivityModalConfig.call(this, networkErrorButtonsCallbacks));
  } else {
    const buttons = [
      {
        ...getGlobalButtonInfo("retry"),
        buttonHandler: retry
      },
      {
        ...getGlobalButtonInfo("back"),
        buttonHandler: back
      }
    ];
    showGlobalModal({
      modalText: i18n.get("global.errors.genericError"),
      buttons
    });
  }
  setFocus(getGlobalButtonFocusPath("retry"));
}

class FullCatalog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      catalogItems: null,
      genres: null,
      isGettingItems: false,
      isDisney: false
    };
    this.onPosterAction = this.onPosterAction.bind(this);
    this.onGenreAction = this.onGenreAction.bind(this);
    this.focusMenu = this.focusMenu.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.onChange = this.onChange.bind(this);
    this.selectedGenre = null;
    this.sectionName = getPath(this.props); // fixme: convert this to generic method;
    this.menu = null;
    this.secondaryMenuSelectedIndex =
      (this.props.history.location.state && this.props.history.location?.state?.secondaryMenuSelectedIndex) || 0;
  }

  /**
   * Redirects to a certain title detail page when the user presses on a title poster.
   */
  onPosterAction() {
    const { history } = this.props;
    const focusedItem = history.location?.state?.focusedItem;
    const title = this.state.catalogItems[focusedItem || 0];
    const { id, tagList } = title;
    const scrollToRow = Math.floor(focusedItem / resolution.grid.fullCatalog.columnCount);
    const sectionName = (history.location.state && history.location?.state?.sectionName) || this.sectionName;

    this.props.stateActions.setPosterLandscapeImage((title && title.images && title.images.posterLandscape) || "");

    this.props.history.replace({
      state: {
        ...history.location.state,
        scrollToRow
      }
    });

    goToDetailPage.call(this, {
      id,
      tagList,
      sectionName,
      sectionAnalytics: `${sectionName}/catalogue`
    });
  }

  /**
   * Callback for when the user presses on a new genre from the list. It will retrieve new section/category
   * data to be rendered in the grid.
   * @param {Number} index - Index from the new genre or category the user pressed on.
   */
  onGenreAction(index) {
    const { history } = this.props;
    this.secondaryMenuSelectedIndex = index;
    const eventData = getFullCatalogEventTrackingData.call(this, this.state.genres[index]);
    trackEvent("catalogue", eventData.action, eventData.label);
    this.getCatalogueItemsByGenreId(this.state.genres[index]);
    this.setState({ isGettingItems: true });
    history.replace({
      state: {
        fullCatalogLastGenre: this.state.genres[index],
        secondaryMenuSelectedIndex: index,
        focusedItem: 0,
        scrollToRow: 0
      }
    });
  }

  onBack() {
    const { history } = this.props;
    history.replace({
      state: {
        fullCatalogLastGenre: null,
        secondaryMenuSelectedIndex: 0,
        scrollToRow: null,
        offsetTopValue: null
      }
    });
    return false;
  }

  processGridFeeds(feeds) {
    return feeds.map(feed => {
      // FIXME Shouldn't this be done in SDK or Middleware?
      feed["imageUrl"] = feed.images.poster;
      return feed;
    });
  }

  async getMovieGenres() {
    let movieGenres;
    try {
      movieGenres = await sdk.mediaManager.getGenres({}, !getAppState().isOnline);
    } catch (error) {
      Sentry.captureException(error);
      handleError.call(this, error);
      return false;
    }
    // This check is made due to movieGenres array persistence and to avoid pushing
    // extra "Disney" object if it already exists. A way to check this behaviour is by commenting
    // this .find() and going from movies full catalog to detail page and back to catalog.
    // !movieGenres.find(el => el.tagTitleLocalized.en === "Disney") &&
    //   movieGenres.push({
    //     tagTitle: i18n.get("pages.fullCatalog.disneySection"),
    //     tagTitleLocalized: { en: "Disney" },
    //     sort: LAST_ADDED
    //   });
    return movieGenres;
  }

  async setMenuItems() {
    const genres = this.sectionName === "movies" ? await this.getMoviesMenuItems() : getDefaultMenuItems();
    this.setState({ genres });
  }

  async getMoviesMenuItems() {
    const movieGenres = await this.getMovieGenres();
    return getDefaultMenuItems().concat(movieGenres);
  }

  /**
   * Retrieves catalogue items by section name value and calls grid render update method to show new titles.
   * @param {Object} params - Object containing pagination and sorting related data.
   * @param {String|Boolean} isSpecialSubsection - String in case it is an special subsection and false if not.
   */
  async getCatalogueItems(params, isSpecialSubsection) {
    try {
      const section = isSpecialSubsection || this.sectionName;
      params.catalogueFeedKey =
        this.state.brand?.configuration?.catalogueFeedKey || this.state.subscription?.configuration?.catalogueFeedKey;
      let catalogItems = await sdk.mediaManager.getCatalogueItems(section, params, !getAppState().isOnline);
      this.update(catalogItems);
    } catch (error) {
      Sentry.captureException(error);
      handleError.call(this, error);
    }
  }

  /**
   * Checks the data coming from the genre selection to proceed with catalogue items retrieval either
   * by passing genre id (if exists) or just by section name.
   * @param {Object} genre - Genre selected.
   */
  async getCatalogueItemsByGenreId(genre) {
    try {
      const isSpecialSubsection = this.checkIfDisneySubSection(genre.tagTitleLocalized);
      this.selectedGenre = genre.tagId || genre.sort;
      if (genre.tagId) {
        const catalogItems = await sdk.mediaManager.getCatalogueItemsByGenreId(genre.tagId, {
          feedUrl: encodeURIComponent(genre.tagModuleUrlTitleAdded),
          sort: LAST_ADDED
        });
        this.update(catalogItems);
      } else {
        this.getCatalogueItems(
          {
            sort: genre.sort
          },
          isSpecialSubsection
        );
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error("Couldn't retrieve genre service");
    }
  }

  // TODO: Manage special subsections in fullcatalog from config files.
  checkIfDisneySubSection(tagTitle) {
    const isDisneyTagTitle = tagTitle && tagTitle.en && tagTitle.en.toLowerCase() === "disney";
    this.setState({ isDisney: isDisneyTagTitle });
    return isDisneyTagTitle && "disney";
  }

  /**
   * Sets and makes page re-render a new bunch of titles retrieved from a genre/category selection change.
   * @param {Array} items - Array of new genre/category titles.
   */
  update(items) {
    const catalogItems = this.processGridFeeds(items);
    this.setState(
      {
        catalogItems,
        isGettingItems: false
      },
      () => this.props.setFocus(`${items.length ? defaultFocusPath : this.menu.getSelectedItem()}`)
    );
  }

  focusMenu() {
    this.menu && this.props.setFocus(this.menu.getSelectedItem());
  }

  onChange({ focusedItem }) {
    const { location, history } = this.props;
    if (focusedItem !== location?.state?.focusedItem) {
      history.replace({
        state: {
          ...location.state,
          focusedItem,
          scrollToRow: Math.floor(focusedItem / resolution.grid.fullCatalog.columnCount)
        }
      });
    }
  }

  async componentDidMount() {
    const { onRef } = this.props;
    const subscription = (this.props.applicationState.subscriptions || []).find(s => s.name === this.sectionName) || {};
    const baseSubscription = (this.props.applicationState.subscriptions || []).find(s => s.name === "starzplay") || {};
    let brand = [];
    if (baseSubscription) {
      brand = (baseSubscription.brands || []).find(s => s.name === this.sectionName) || {};
      this.setState({ brand });
    }
    this.setState({
      subscription: baseSubscription?.name === "starzplay" && brand.name === this.sectionName ? brand : subscription
    });
    trackPage.call(this, PAGE);
    onRef && onRef(this);
    await this.fetchData();
  }

  async fetchData() {
    sdk = await getSdk();
    const { history } = this.props;
    const lastGenre = history.location.state && history.location?.state?.fullCatalogLastGenre;
    await this.setMenuItems();
    if (lastGenre) {
      this.getCatalogueItemsByGenreId(lastGenre);
    } else {
      this.getCatalogueItems({
        sort: LAST_ADDED
      });
    }
  }

  render() {
    const { location } = this.props;
    const { catalogItems, genres, isDisney, isGettingItems } = this.state;
    return (
      <FullCatalogPageStyled
        background={
          this.state.isDisney &&
          "http://mena-img-cdn-lb.aws.playco.com/prd-peg-data/default/bigscreen/images/Themes/disney/background.jpg"
        }
      >
        <HeadingStyled
          rank={2}
          text={this.state.subscription?.displayName || i18n.get(`global.sections.${this.sectionName}`)}
        />
        <ContainerStyled>
          {genres && (
            <BlockStyled>
              <SecondaryMenuStyled
                items={genres}
                onClick={this.onGenreAction}
                onRef={menu => (this.menu = menu)}
                secondaryMenuSelectedIndex={this.secondaryMenuSelectedIndex}
                dataSnDown=""
              />
            </BlockStyled>
          )}
          {isGettingItems ? (
            <Loading overlay />
          ) : (
            <BlockStyled>
              {catalogItems ? (
                <GridStyled
                  focusPath={defaultFocusPath}
                  titles={this.state.catalogItems}
                  noResultText={i18n.get("pages.fullCatalog.noResults")}
                  onEnterPress={this.onPosterAction}
                  columnCount={resolution.grid.fullCatalog.columnCount}
                  columnWidth={resolution.grid.fullCatalog.columnWidth}
                  itemHeight={resolution.grid.fullCatalog.itemHeight}
                  rowHeight={resolution.grid.fullCatalog.rowHeight}
                  gridDataContent={this.selectedGenre}
                  offsetFixed={isDisney}
                  navigateLeft={i18n.getDirection() === directions.LEFT_TO_RIGHT && this.focusMenu}
                  navigateRight={i18n.getDirection() === directions.RIGHT_TO_LEFT && this.focusMenu}
                  direction={i18n.getDirection()}
                  onChange={this.onChange}
                  scrollToRow={location.state && location?.state?.scrollToRow ? location?.state?.scrollToRow : 0}
                  focusedItem={location.state && location?.state?.focusedItem ? location?.state?.focusedItem : 0}
                  inactive={isGettingItems}
                >
                  {isDisney && <ImageStyled src="../../images/disney.svg" alt="Disney" />}
                  {isGettingItems && <Loading overlay opacity="0" />}
                </GridStyled>
              ) : (
                <Loading overlay />
              )}
            </BlockStyled>
          )}
        </ContainerStyled>
      </FullCatalogPageStyled>
    );
  }
}

const Component = withAppState(withSubtheme(FullCatalog));

Component.defaultFocusPath = defaultFocusPath;

export default Component;
